import { z } from "zod";
const TenantSlugSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9-]+$/);
export const AdminAuditLogQuerySchema = z.object({
    resource: z.string().trim().min(1).max(50).optional(),
    action: z.string().trim().min(1).max(64).optional(),
    userId: z.string().trim().min(1).max(320).optional(),
    before: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const AdminAuditLogEntrySchema = z.object({
    id: z.string().uuid(),
    resource: z.string(),
    resourceId: z.string(),
    action: z.string(),
    userId: z.string(),
    details: z.record(z.string(), z.unknown()).nullable(),
    ipAddress: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const AdminAuditLogListResponseSchema = z.object({
    audit: z.array(AdminAuditLogEntrySchema),
    hasMore: z.boolean(),
});
export const TenantDataExportFormatSchema = z.enum(["json", "csv"]);
export const TenantDataExportQuerySchema = z.object({
    format: TenantDataExportFormatSchema.default("json"),
});
export const TenantDataExportTableSchema = z.object({
    table: z.string().min(1),
    rowCount: z.number().int().nonnegative(),
    rows: z.array(z.record(z.string(), z.unknown())),
});
export const TenantDataExportResponseSchema = z.object({
    tenantSlug: TenantSlugSchema,
    exportedAt: z.string().datetime(),
    generatedBy: z.string().email(),
    format: z.literal("json"),
    tables: z.array(TenantDataExportTableSchema),
});
export const TenantDeletionStatusSchema = z.enum(["pending", "cancelled", "hard_deleted"]);
export const TenantDeletionRequestSchema = z.object({
    id: z.string().uuid(),
    tenantSlug: TenantSlugSchema,
    status: TenantDeletionStatusSchema,
    requestedBy: z.string().email(),
    requestedAt: z.string().datetime(),
    scheduledHardDeleteAt: z.string().datetime(),
    reason: z.string().nullable(),
    cancelledBy: z.string().email().nullable(),
    cancelledAt: z.string().datetime().nullable(),
    completedBy: z.string().email().nullable(),
    completedAt: z.string().datetime().nullable(),
});
export const TenantDeletionScheduleSchema = z.object({
    confirmTenantSlug: TenantSlugSchema,
    retentionDays: z.coerce.number().int().min(7).max(365).default(30),
    reason: z.string().trim().max(1000).optional(),
});
export const TenantDeletionStatusResponseSchema = z.object({
    tenantSlug: TenantSlugSchema,
    pending: TenantDeletionRequestSchema.nullable(),
    history: z.array(TenantDeletionRequestSchema),
});
export const TenantDeletionMutationResponseSchema = z.object({
    request: TenantDeletionRequestSchema,
});
// ─── Smoke-data purge (dev-only, admin-gated) ──────────────────────────────
//
// Post-deploy Playwright smokes create `smoke-`-prefixed rows on the deployed
// dev tenant. This endpoint purges them, dry-run by default. Blocked outright
// in production (see the route's NODE_ENV guard) — the query schema itself
// doesn't need to know about that; it just shapes the dry-run/apply toggle.
// `z.coerce.boolean()` is `Boolean(value)`, and `Boolean("false") === true` in
// JS — so `?apply=false` (an operator explicitly requesting a dry-run) would
// coerce to `true` and trigger a real bulk delete. Only the literal string
// `"true"` may enable `apply`; absent, `"false"`, and any garbage value all
// mean dry-run.
export const PurgeSmokeDataQuerySchema = z.object({
    apply: z
        .string()
        .optional()
        .transform((v) => v === "true"),
});
// Loose response: `counts` keys are table names, intentionally untyped as a
// closed enum so a future table added to the purge order doesn't require a
// schema change to stay valid (canon: response schemas loose, request schemas
// tight).
export const SmokePurgeResultSchema = z.object({
    dryRun: z.boolean(),
    counts: z.record(z.string(), z.number()),
    total: z.number(),
});
/** Staff start/stop for the T33 PoC Flexible Server only — never Insights Postgres. */
export const T33PocPostgresStateSchema = z.enum([
    "Ready",
    "Stopped",
    "Starting",
    "Stopping",
    "Updating",
    "Unknown",
]);
export const T33PocPostgresStatusSchema = z.object({
    configured: z.boolean(),
    serverName: z.string().nullable(),
    state: T33PocPostgresStateSchema.nullable(),
    reason: z.string().nullable(),
});
export const T33PocPostgresMutationResponseSchema = T33PocPostgresStatusSchema.extend({
    accepted: z.boolean(),
});
//# sourceMappingURL=admin.js.map